/**
 * Auth Storage Utility
 *
 * Provides persistent authentication storage that works reliably across all platforms:
 * - Web browsers: Uses localStorage (standard, works well)
 * - Mobile apps (iOS/Android): Uses Capacitor Preferences (native, survives app restarts)
 *
 * The Capacitor Preferences API uses:
 * - iOS: UserDefaults (persistent, survives app updates)
 * - Android: SharedPreferences (persistent, survives app restarts)
 *
 * This ensures SSO sessions persist for the full 30-day token lifetime on all devices.
 */

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

/* ============================================================================
   CONSTANTS
   ============================================================================ */

const AUTH_STORAGE_KEY = 'user';
const PENDING_DEEP_LINK_KEY = 'pendingDeepLink';

/* ============================================================================
   PLATFORM DETECTION
   ============================================================================ */

/**
 * Check if we're running on a native mobile platform (iOS/Android)
 * Returns false for web browsers, including mobile browsers
 */
export function isNativeMobile(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    // Capacitor not available (web-only build)
    return false;
  }
}

/* ============================================================================
   STORAGE OPERATIONS
   ============================================================================ */

/**
 * Store the user authentication data
 * Uses Capacitor Preferences on native mobile, localStorage on web
 */
export async function setAuthUser<T extends object>(userData: T): Promise<void> {
  const value = JSON.stringify(userData);

  if (isNativeMobile()) {
    // Native mobile: Use Capacitor Preferences (persistent native storage)
    await Preferences.set({ key: AUTH_STORAGE_KEY, value });
    // Also set in localStorage for immediate access (sync backup)
    try {
      localStorage.setItem(AUTH_STORAGE_KEY, value);
    } catch {
      // localStorage might fail in some WebView contexts, that's ok
    }
  } else {
    // Web: Use localStorage
    localStorage.setItem(AUTH_STORAGE_KEY, value);
  }
}

/**
 * Get the stored user authentication data
 * Checks Capacitor Preferences first on mobile, falls back to localStorage
 */
export async function getAuthUser<T = unknown>(): Promise<T | null> {
  try {
    if (isNativeMobile()) {
      // Native mobile: Try Capacitor Preferences first (more reliable)
      const result = await Preferences.get({ key: AUTH_STORAGE_KEY });
      if (result.value) {
        const parsed = JSON.parse(result.value) as T;
        // Sync to localStorage for immediate access in future sync calls
        try {
          localStorage.setItem(AUTH_STORAGE_KEY, result.value);
        } catch {
          // Ignore localStorage errors
        }
        return parsed;
      }

      // Fall back to localStorage (for migration from old sessions)
      const localValue = localStorage.getItem(AUTH_STORAGE_KEY);
      if (localValue) {
        const parsed = JSON.parse(localValue) as T;
        // Migrate to Preferences for future persistence
        await Preferences.set({ key: AUTH_STORAGE_KEY, value: localValue });
        return parsed;
      }

      return null;
    } else {
      // Web: Use localStorage
      const value = localStorage.getItem(AUTH_STORAGE_KEY);
      return value ? (JSON.parse(value) as T) : null;
    }
  } catch {
    return null;
  }
}

/**
 * Get the stored user synchronously (localStorage only)
 * Use this for quick sync checks, but prefer getAuthUser() for reliability on mobile
 */
export function getAuthUserSync<T = unknown>(): T | null {
  try {
    const value = localStorage.getItem(AUTH_STORAGE_KEY);
    return value ? (JSON.parse(value) as T) : null;
  } catch {
    return null;
  }
}

/**
 * Remove the user authentication data (logout)
 * Clears both Capacitor Preferences and localStorage
 */
export async function removeAuthUser(): Promise<void> {
  if (isNativeMobile()) {
    // Clear from both storage locations
    await Preferences.remove({ key: AUTH_STORAGE_KEY });
  }
  // Always clear localStorage
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // Ignore errors
  }
  // And the service worker's API cache: it is keyed by URL alone, so the next
  // person on this device would be served this one's responses offline. A
  // renamed cache is never deleted by Workbox, so clear every generation.
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.filter(k => k.startsWith('api-cache')).map(k => caches.delete(k)));
    }
  } catch {
    // Ignore errors: signing out must not fail on a cache
  }
}

/**
 * Remove auth user synchronously (localStorage only)
 * Use removeAuthUser() when possible for complete cleanup on mobile
 */
export function removeAuthUserSync(): void {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // Ignore errors
  }
}

/* ============================================================================
   DEEP LINK STORAGE
   ============================================================================ */

/**
 * Store a pending deep link to restore after authentication
 */
export async function setPendingDeepLink(url: string): Promise<void> {
  if (isNativeMobile()) {
    await Preferences.set({ key: PENDING_DEEP_LINK_KEY, value: url });
  }
  try {
    localStorage.setItem(PENDING_DEEP_LINK_KEY, url);
  } catch {
    // Ignore errors
  }
}

/**
 * Get and clear the pending deep link
 */
export async function consumePendingDeepLink(): Promise<string | null> {
  let value: string | null = null;

  if (isNativeMobile()) {
    const result = await Preferences.get({ key: PENDING_DEEP_LINK_KEY });
    value = result.value;
    if (value) {
      await Preferences.remove({ key: PENDING_DEEP_LINK_KEY });
    }
  }

  // Also check localStorage
  const localValue = localStorage.getItem(PENDING_DEEP_LINK_KEY);
  if (localValue) {
    localStorage.removeItem(PENDING_DEEP_LINK_KEY);
    value = value || localValue;
  }

  return value;
}

/**
 * Remove pending deep link
 */
export async function removePendingDeepLink(): Promise<void> {
  if (isNativeMobile()) {
    await Preferences.remove({ key: PENDING_DEEP_LINK_KEY });
  }
  try {
    localStorage.removeItem(PENDING_DEEP_LINK_KEY);
  } catch {
    // Ignore errors
  }
}

/* ============================================================================
   INITIALIZATION
   ============================================================================ */

/**
 * Initialize auth storage - migrates existing localStorage data to Preferences on mobile
 * Call this once when the app starts on native platforms
 */
export async function initializeAuthStorage(): Promise<void> {
  if (!isNativeMobile()) return;

  try {
    // Check if we have data in localStorage that needs migration
    const localUser = localStorage.getItem(AUTH_STORAGE_KEY);
    if (localUser) {
      const preferencesResult = await Preferences.get({ key: AUTH_STORAGE_KEY });
      if (!preferencesResult.value) {
        // Migrate localStorage to Preferences
        await Preferences.set({ key: AUTH_STORAGE_KEY, value: localUser });
        console.info('[AuthStorage] Migrated auth data to native storage');
      }
    }

    // Same for deep links
    const localDeepLink = localStorage.getItem(PENDING_DEEP_LINK_KEY);
    if (localDeepLink) {
      const preferencesResult = await Preferences.get({ key: PENDING_DEEP_LINK_KEY });
      if (!preferencesResult.value) {
        await Preferences.set({ key: PENDING_DEEP_LINK_KEY, value: localDeepLink });
      }
    }
  } catch (error) {
    console.error('[AuthStorage] Migration error:', error);
  }
}

/* ============================================================================
   TOKEN HELPERS
   ============================================================================ */

interface AuthUserWithToken {
  token?: string;
  access_token?: string;
  id_token?: string;
}

/**
 * Get the auth token from stored user (async version for mobile)
 */
export async function getStoredToken(): Promise<string | null> {
  const user = await getAuthUser<AuthUserWithToken>();
  if (!user) return null;
  return user.token || user.access_token || user.id_token || null;
}

/**
 * Get the auth token synchronously (localStorage only)
 * Use getStoredToken() when possible for reliability on mobile
 */
export function getStoredTokenSync(): string | null {
  const user = getAuthUserSync<AuthUserWithToken>();
  if (!user) return null;
  return user.token || user.access_token || user.id_token || null;
}

/**
 * Check if user is currently authenticated (has valid stored session)
 */
export async function isAuthenticated(): Promise<boolean> {
  const token = await getStoredToken();
  return !!token;
}

/**
 * Check if user is authenticated (sync version, localStorage only)
 */
export function isAuthenticatedSync(): boolean {
  const token = getStoredTokenSync();
  return !!token;
}
